type PassPlatform = 'apple' | 'google';
type PassType = 'coupon' | 'loyalty';

interface WalletPassPreviewProps {
  platform: PassPlatform;
  type: PassType;
  businessName: string;
  businessIcon: string;
  heroImage: string;
  // Coupon-specific
  discountText?: string;
  discountSubtext?: string;
  validUntil?: string;
  // Loyalty-specific
  currentStamps?: number;
  totalStamps?: number;
  rewardText?: string;
  // Styling
  backgroundColor: string;
  width?: string;
}

// Reusable QR Code SVG Component
const QRCode = () => (
  <svg width="100%" height="100%" viewBox="0 0 29 29" fill="none" preserveAspectRatio="xMidYMid meet">
    <rect width="29" height="29" fill="white"/>
    <path d="M0 0h7v7h-7zM8 0v1h1v2h-1v1h-1v-1h-1v-1h1v-1h-1v-1zM10 0h1v1h-1zM12 0h1v1h1v1h-1v3h-1v1h-2v-1h1v-2h1zM15 0h2v2h-1v-1h-1zM18 0h3v1h1v-1h1v2h-2v-1h-1v1h-1v1h-1zM22 0h7v7h-7zM1 1v5h5v-5zM23 1v5h5v-5zM2 2h3v3h-3zM24 2h3v3h-3z" fill="black"/>
  </svg>
);

export const WalletPassPreview = ({
  platform,
  type,
  businessName,
  businessIcon,
  heroImage,
  discountText,
  discountSubtext,
  validUntil,
  currentStamps = 5,
  totalStamps = 8,
  rewardText,
  backgroundColor,
  width
}: WalletPassPreviewProps) => {
  // Platform-specific styling
  const isApple = platform === 'apple';
  const borderRadius = isApple ? '8px' : '14px';
  const boxShadow = isApple
    ? '0 8px 25px rgba(0, 0, 0, 0.15), 0 3px 8px rgba(0, 0, 0, 0.1)'
    : '0 6px 20px rgba(0, 0, 0, 0.12)';
  const iconSize = isApple ? 'w-[22px] h-[22px]' : 'w-4 h-4';
  const iconTextSize = isApple ? 'text-[8px]' : 'text-[6px]';
  const headerTextSize = isApple ? 'text-[8px]' : 'text-[7px]';
  const headerFontWeight = isApple ? 'font-medium' : 'font-normal';
  const qrPadding = isApple ? 'p-1.5' : 'px-1.5 py-1.5';
  const qrRounding = isApple ? 'rounded-sm' : 'rounded-lg';
  const passWidth = width || (isApple ? '140px' : '125px');

  // Type-specific content
  const isCoupon = type === 'coupon';
  const programName = isCoupon ? 'Review Reward' : 'Loyalty Card';

  return (
    <div
      className="relative overflow-hidden flex flex-col"
      style={{
        width: passWidth,
        backgroundColor,
        borderRadius,
        aspectRatio: '270/424.766',
        boxShadow
      }}
    >
      {/* Header */}
      <div className={`flex items-center ${isApple ? 'px-2 pt-1.5 pb-1' : 'p-2 pb-1'}`}>
        <div className="flex items-center gap-1.5">
          <div className={`${iconSize} ${isApple ? 'rounded' : 'rounded-full'} bg-white/90 flex items-center justify-center`}>
            <span style={{ color: backgroundColor }} className={`${iconTextSize} font-bold`}>{businessIcon}</span>
          </div>
          <span className={`text-white ${headerTextSize} ${headerFontWeight}`}>{businessName}</span>
        </div>
      </div>

      {/* Google: Divider + Program Name */}
      {!isApple && (
        <>
          <div className="w-full h-px bg-white/20"></div>
          <div className={`px-2 ${isCoupon ? 'pt-2 pb-1' : 'pt-1.5 pb-1'}`}>
            <h2 className={`text-white ${isCoupon ? 'text-[10px]' : 'text-[9px]'} font-medium`}>
              {programName}
            </h2>
          </div>
        </>
      )}

      {/* Apple: Hero Image Top (Coupon only for Apple) */}
      {isApple && isCoupon && (
        <div className="w-full overflow-hidden" style={{ aspectRatio: '125/48' }}>
          <img src={heroImage} alt={businessName} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Apple: Hero Image Top (Loyalty) */}
      {isApple && !isCoupon && (
        <div className="w-full overflow-hidden" style={{ aspectRatio: '125/48' }}>
          <img src={heroImage} alt={businessName} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Coupon Content */}
      {isCoupon && (
        <div className={`${isApple ? 'px-2 pt-3 pb-2' : 'px-2 pt-1 pb-2'} text-center`}>
          <div className={`text-white ${isApple ? 'text-xl' : 'text-base'} font-bold tracking-tight`}>
            {discountText || '15% OFF'}
          </div>
          {isApple && (
            <div className="text-white/80 text-[8px] mt-1">
              {discountSubtext || 'Your next visit'}
            </div>
          )}
          <div className="text-white/60 text-[6px] mt-0.5">
            {validUntil || 'Valid until Dec 31, 2025'}
          </div>
        </div>
      )}

      {/* Loyalty Content - Stamp Grid */}
      {!isCoupon && (
        <>
          <div className={`${isApple ? 'px-2 pt-2 pb-1' : 'px-2 pt-1 pb-1'}`}>
            <div className={`grid grid-cols-4 ${isApple ? 'gap-1' : 'gap-0.5'}`}>
              {Array.from({ length: totalStamps }).map((_, i) => (
                <div
                  key={i}
                  className={`${isApple ? 'w-5 h-5' : 'w-4 h-4'} rounded-full border flex items-center justify-center ${
                    i < currentStamps
                      ? 'border-white bg-white/30'
                      : 'border-white/30 bg-white/5'
                  }`}
                >
                  {i < currentStamps && (
                    <span className={`text-white ${isApple ? 'text-[8px]' : 'text-[6px]'}`}>✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Progress text */}
          <div className={`px-2 ${isApple ? 'pt-1 pb-1' : 'pt-0.5'}`}>
            <div className={`text-white ${isApple ? 'text-sm' : 'text-[10px]'} font-bold`}>
              {isApple ? `${currentStamps}/${totalStamps}` : `${currentStamps}/${totalStamps} stamps`}
            </div>
            {isApple && (
              <div className="text-white/60 text-[6px]">
                {rewardText || `${totalStamps - currentStamps} more for free coffee`}
              </div>
            )}
          </div>
        </>
      )}

      {/* Spacer (different for each variant) */}
      {isApple && isCoupon && <div style={{ flex: '0.3' }} />}
      {isApple && !isCoupon && <div style={{ flex: '0.1' }} />}
      {!isApple && <div className="flex-1" />}

      {/* QR Code */}
      <div className={`${isApple ? 'px-2 pb-2' : isCoupon ? 'px-2 pb-2' : 'px-2 py-1.5'} flex justify-center`}>
        <div className={`bg-white ${qrRounding} ${qrPadding} flex items-center justify-center`} style={{ width: isApple ? '50%' : (isCoupon ? '45%' : '40%') }}>
          <div style={{ width: '100%', aspectRatio: '1' }}>
            <QRCode />
          </div>
        </div>
      </div>

      {/* Google: Hero Image at Bottom */}
      {!isApple && (
        <div className="w-full overflow-hidden" style={{ aspectRatio: '125/48' }}>
          <img
            src={heroImage}
            alt={businessName}
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  );
};
